/** @format */

import { useEffect } from "react";
import { create } from "zustand";

import { type AppConfig, useConfig } from "@/api/configApi";

interface ConfigStore {
  config: AppConfig | undefined;
  isLoading: boolean;
  error: Error | null;
  // Actions to update from React Query
  setConfig: (config: AppConfig | undefined) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;
}

/**
 * Zustand store for application configuration.
 * This store integrates with React Query's useConfig hook for data fetching.
 * Use the convenience hooks (useAppConfig, useIsLocalRuntime, etc.) for accessing config values.
 */
export const useConfigStore = create<ConfigStore>(set => ({
  config: undefined,
  isLoading: true,
  error: null,
  setConfig: config => set({ config }),
  setLoading: isLoading => set({ isLoading }),
  setError: error => set({ error }),
}));

/**
 * Hook to sync React Query config data with zustand store.
 * This should be called at the app root level to keep the store in sync.
 */
export const useSyncConfigStore = () => {
  const { data: config, isLoading, error } = useConfig();
  const setConfig = useConfigStore(state => state.setConfig);
  const setLoading = useConfigStore(state => state.setLoading);
  const setError = useConfigStore(state => state.setError);

  useEffect(() => {
    setConfig(config);
    setLoading(isLoading);
    setError(error ?? null);
  }, [config, isLoading, error, setConfig, setLoading, setError]);
};
